import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export function NewsletterCycleSetup({
  cycle,
  groups,
  busy,
  recipients,
  onSave,
  onPreview,
  onClose,
}) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(cycle?.start_date || "");
  const [weeks, setWeeks] = useState(String(cycle?.weeks || 1));
  const [selected, setSelected] = useState(() => new Set(cycle?.group_ids || []));
  const [confirmClose, setConfirmClose] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setStartDate(cycle?.start_date || "");
    setWeeks(String(cycle?.weeks || 1));
    setSelected(new Set(cycle?.group_ids || []));
  }, [cycle]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const payload = () => ({
    start_date: startDate,
    weeks: Number(weeks),
    group_ids: [...selected],
  });

  const run = async (action) => {
    setError("");
    if (!startDate) {
      setError(t("newsletterStartRequired", "Choose a start date"));
      return;
    }
    if (!selected.size) {
      setError(t("newsletterGroupsRequired", "Choose at least one group"));
      return;
    }
    try {
      await action(payload());
      setConfirmClose(false);
    } catch (err) {
      setError(err.message || t("newsletterError", "Could not update the cycle"));
    }
  };

  return (
    <section className="pingflo-setup">
      <h2>{t("newsletterSetup", "Cycle")}</h2>
      <label>
        {t("newsletterStart", "Start date")}
        <input
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
      </label>
      <label>
        {t("newsletterLength", "Length")}
        <select value={weeks} onChange={(event) => setWeeks(event.target.value)}>
          {[1, 2, 3, 4].map((count) => (
            <option key={count} value={count}>
              {count === 1
                ? t("newsletterOneWeekWeekly", "1 week (weekly issue)")
                : t("newsletterWeekCount", { count, defaultValue: "{{count}} weeks" })}
            </option>
          ))}
        </select>
      </label>
      <fieldset>
        <legend>{t("newsletterGroups", "Groups to follow")}</legend>
        {groups?.length ? (
          groups.map((group) => (
            <label key={group.gr_id} className="pingflo-check">
              <input
                type="checkbox"
                checked={selected.has(group.gr_id)}
                onChange={() => toggle(group.gr_id)}
              />
              {group.gr_name || group.gr_id}
            </label>
          ))
        ) : (
          <p className="pingflo-quiet">{t("newsletterNoGroups", "This club has no groups yet")}</p>
        )}
      </fieldset>
      {recipients ? (
        <div className="pingflo-recipients">
          <p>
            <strong>
              {t("newsletterRecipientCount", {
                count: recipients.count,
                defaultValue: "This issue will go to {{count}} members",
              })}
            </strong>
            {recipients.optouts ? (
              <span className="pingflo-quiet">
                {" · "}
                {t("newsletterOptouts", {
                  count: recipients.optouts,
                  defaultValue: "{{count}} unsubscribed",
                })}
              </span>
            ) : null}
          </p>
          <p className="pingflo-quiet">
            {recipients.sender?.club
              ? t("newsletterSenderClub", {
                  email: recipients.sender.from_address,
                  defaultValue: "Sent from {{email}}",
                })
              : t("newsletterSenderPlatform", "Sent from the platform address until a club mailbox is set up")}
          </p>
        </div>
      ) : null}
      {error ? <p className="pingflo-error">{error}</p> : null}
      <div className="pingflo-actions">
        <button type="button" disabled={busy} onClick={() => run(onSave)}>
          {t("newsletterSave", "Save cycle")}
        </button>
        <button type="button" disabled={busy} onClick={() => run(onPreview)}>
          {t("newsletterPreview", "Preview")}
        </button>
        {confirmClose ? (
          <button type="button" className="is-send" disabled={busy} onClick={() => run(() => onClose())}>
            {t("newsletterCloseConfirm", "Close this cycle, publish the issue, and email club members")}
          </button>
        ) : (
          <button type="button" className="is-send" disabled={busy || !cycle} onClick={() => setConfirmClose(true)}>
            {t("newsletterClose", "Close cycle and send")}
          </button>
        )}
      </div>
    </section>
  );
}
